import * as THREE from 'three';

import {
  DEFAULT_LIMITED_MAX_DEGREES,
  DEFAULT_LIMITED_MIN_DEGREES,
} from '../../../../lib/skeleton/rotationLimits';
import {
  UNLIMITED_HANDLE_MAX_DEGREES,
  UNLIMITED_HANDLE_MIN_DEGREES,
} from './constants';
import type { ManipulatorAxis } from '../types';
import {
  angleDegreesFromLocalPoint,
  axisIndexFromKey,
  axisNormalVector,
  axisPointFromDegrees,
  normalizeDegrees,
} from '../utils';

export {
  angleDegreesFromLocalPoint,
  axisIndexFromKey,
  axisNormalVector,
  axisPointFromDegrees,
  normalizeDegrees,
};

const TWO_PI = Math.PI * 2;
const MIN_SWEEP_RADIANS = 1e-3;

function axisPointFromRadians(
  axis: ManipulatorAxis,
  angleRadians: number,
  radius: number,
): THREE.Vector3 {
  const cos = Math.cos(angleRadians) * radius;
  const sin = Math.sin(angleRadians) * radius;

  switch (axis) {
    case 'x':
      return new THREE.Vector3(0, cos, sin);
    case 'y':
      return new THREE.Vector3(sin, 0, cos);
    case 'z':
      return new THREE.Vector3(cos, sin, 0);
  }
}

export function axisTangentFromDegrees(
  axis: ManipulatorAxis,
  angleDegrees: number,
): THREE.Vector3 {
  const radial = axisPointFromDegrees(axis, angleDegrees, 1).normalize();
  return axisNormalVector(axis).clone().cross(radial).normalize();
}

export function resolveDisplayBounds(
  mode: 'unlimited' | 'limited' | 'locked',
  min: number | null,
  max: number | null,
): { min: number; max: number } {
  if (mode === 'unlimited') {
    return {
      min: UNLIMITED_HANDLE_MIN_DEGREES,
      max: UNLIMITED_HANDLE_MAX_DEGREES,
    };
  }

  return {
    min: min ?? DEFAULT_LIMITED_MIN_DEGREES,
    max: max ?? DEFAULT_LIMITED_MAX_DEGREES,
  };
}

export function isAngleInArc(
  angleDegrees: number,
  startDegrees: number,
  endDegrees: number,
): boolean {
  let sweep = endDegrees - startDegrees;
  while (sweep < 0) sweep += 360;
  if (sweep < 0.01) sweep = 360;

  let offset = angleDegrees - startDegrees;
  while (offset < -1e-3) offset += 360;
  while (offset > 360 + 1e-3) offset -= 360;

  return offset <= sweep + 1e-3;
}

function computeArcSweepRadians(
  startDegrees: number,
  endDegrees: number,
): { startRadians: number; sweepRadians: number } {
  const startRadians = THREE.MathUtils.degToRad(startDegrees);
  const endRadians = THREE.MathUtils.degToRad(endDegrees);

  let sweepRadians = endRadians - startRadians;
  if (sweepRadians < 0) sweepRadians += TWO_PI;
  if (sweepRadians < MIN_SWEEP_RADIANS) {
    sweepRadians = TWO_PI;
  }

  return { startRadians, sweepRadians };
}

export function createAxisSweepGeometry({
  axis,
  startDegrees,
  endDegrees,
  radius,
  segmentCount,
}: {
  axis: ManipulatorAxis;
  startDegrees: number;
  endDegrees: number;
  radius: number;
  segmentCount: number;
}): THREE.BufferGeometry {
  const { startRadians, sweepRadians } = computeArcSweepRadians(
    startDegrees,
    endDegrees,
  );
  const safeSegments = Math.max(
    10,
    Math.ceil((segmentCount * sweepRadians) / TWO_PI),
  );

  const positions: number[] = [0, 0, 0];
  const indices: number[] = [];

  for (let i = 0; i <= safeSegments; i++) {
    const t = i / safeSegments;
    const angle = startRadians + sweepRadians * t;
    const point = axisPointFromRadians(axis, angle, radius);
    positions.push(point.x, point.y, point.z);
  }

  for (let i = 1; i <= safeSegments; i++) {
    indices.push(0, i, i + 1);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createAxisRibbonGeometry({
  axis,
  startDegrees,
  endDegrees,
  radius,
  halfWidth,
  segmentCount,
}: {
  axis: ManipulatorAxis;
  startDegrees: number;
  endDegrees: number;
  radius: number;
  halfWidth: number;
  segmentCount: number;
}): THREE.BufferGeometry {
  const { startRadians, sweepRadians } = computeArcSweepRadians(
    startDegrees,
    endDegrees,
  );
  const safeSegments = Math.max(
    10,
    Math.ceil((segmentCount * sweepRadians) / TWO_PI),
  );

  const positions: number[] = [];
  const indices: number[] = [];
  const axisNormal = axisNormalVector(axis).multiplyScalar(Math.abs(halfWidth));

  for (let i = 0; i <= safeSegments; i += 1) {
    const t = i / safeSegments;
    const angle = startRadians + sweepRadians * t;

    const centerPoint = axisPointFromRadians(axis, angle, radius);
    const outerPoint = centerPoint.clone().add(axisNormal);
    const innerPoint = centerPoint.clone().sub(axisNormal);

    positions.push(
      outerPoint.x,
      outerPoint.y,
      outerPoint.z,
      innerPoint.x,
      innerPoint.y,
      innerPoint.z,
    );

    if (i === safeSegments) continue;

    const outerA = i * 2;
    const innerA = outerA + 1;
    const outerB = outerA + 2;
    const innerB = outerA + 3;
    indices.push(outerA, innerA, outerB, innerA, innerB, outerB);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createAxisRibbonEdgeGeometry({
  axis,
  startDegrees,
  endDegrees,
  radius,
  normalOffset,
  segmentCount,
}: {
  axis: ManipulatorAxis;
  startDegrees: number;
  endDegrees: number;
  radius: number;
  normalOffset: number;
  segmentCount: number;
}): THREE.BufferGeometry {
  const { startRadians, sweepRadians } = computeArcSweepRadians(
    startDegrees,
    endDegrees,
  );
  const safeSegments = Math.max(
    10,
    Math.ceil((segmentCount * sweepRadians) / TWO_PI),
  );

  const positions: number[] = [];
  const normalOffsetVector = axisNormalVector(axis).multiplyScalar(normalOffset);
  let previousPoint: THREE.Vector3 | null = null;

  for (let i = 0; i <= safeSegments; i += 1) {
    const t = i / safeSegments;
    const angle = startRadians + sweepRadians * t;
    const point = axisPointFromRadians(axis, angle, radius).add(normalOffsetVector);
    if (previousPoint) {
      positions.push(
        previousPoint.x,
        previousPoint.y,
        previousPoint.z,
        point.x,
        point.y,
        point.z,
      );
    }
    previousPoint = point;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  return geometry;
}
